import React, { useState } from 'react';
import { useSEO } from '../../hooks/useSEO';
import './Features.scss';

interface Feature {
  id: number;
  title: string;
  description: string;
  icon: string;
  premium?: boolean;
  highlight?: boolean;
  category: 'comercial' | 'financeiro' | 'operacional' | 'fiscal' | 'administrativo' | 'integracao';
}

const Features: React.FC = () => {
  useSEO({
    title: 'Funcionalidades do 77Gestão | ERP Completo para Pequenas Empresas',
    description: 'Conheça todos os módulos do 77Gestão: financeiro, comercial, fiscal, estoque, ordens de serviço, NF-e e muito mais. Gestão completa em um único sistema.',
    canonical: '/funcionalidades',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: '77Gestão',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://77gestao.com.br/funcionalidades',
      description: 'Sistema ERP completo com módulos de gestão financeira, comercial, fiscal, estoque e operacional para pequenas empresas.',
    },
  });

  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'Todas', icon: 'fa-th' },
    { id: 'comercial', name: 'Comercial', icon: 'fa-handshake' },
    { id: 'financeiro', name: 'Financeiro', icon: 'fa-dollar-sign' },
    { id: 'operacional', name: 'Operacional', icon: 'fa-cogs' },
    { id: 'fiscal', name: 'Fiscal', icon: 'fa-file-invoice' },
    { id: 'administrativo', name: 'Administrativo', icon: 'fa-briefcase' },
    { id: 'integracao', name: 'Integração', icon: 'fa-plug' }
  ];

  const features: Feature[] = [
    // COMERCIAL
    {
      id: 1,
      title: 'Gestão de Clientes (CRM)',
      description: 'Histórico completo de compras, propostas e negociações em um só lugar — nunca perca o contexto de um cliente.',
      icon: 'fa-users',
      category: 'comercial'
    },
    {
      id: 2,
      title: 'Gestão de Parceiros',
      description: 'Fornecedores, transportadoras e parceiros com histórico de compras, contatos e condições comerciais centralizadas.',
      icon: 'fa-handshake',
      category: 'comercial'
    },
    {
      id: 3,
      title: 'Função de Vendedor',
      description: 'Comissões automáticas por venda, visibilidade de carteira restrita ao vendedor e relatório de performance individual.',
      icon: 'fa-user-tie',
      category: 'comercial',
      premium: true
    },
    {
      id: 4,
      title: 'Propostas Comerciais',
      description: 'Gere propostas profissionais em segundos, acompanhe aprovações e converta em venda com um clique.',
      icon: 'fa-file-signature',
      category: 'comercial'
    },
    {
      id: 5,
      title: 'Gestão de Vendas',
      description: 'Do pedido à entrega: emita NF-e/NFS-e, controle entregas parciais e acompanhe cada venda em tempo real.',
      icon: 'fa-shopping-cart',
      category: 'comercial'
    },
    {
      id: 6,
      title: 'Status de Vendas',
      description: 'Personalize o funil de vendas com status próprios, bloqueie alterações indevidas e ganhe visibilidade total do pipeline.',
      icon: 'fa-tasks',
      category: 'comercial',
      premium: true
    },
    {
      id: 7,
      title: 'PDV (Ponto de Venda)',
      description: 'Frente de caixa ágil com abertura, sangria, fechamento e checkout integrado ao estoque e financeiro.',
      icon: 'fa-cash-register',
      category: 'comercial'
    },
    {
      id: 8,
      title: 'Canais de Negociação',
      description: 'Separe e analise vendas por canal — loja física, WhatsApp, e-commerce, representantes — e saiba onde vende mais.',
      icon: 'fa-sitemap',
      category: 'comercial'
    },
    {
      id: 9,
      title: 'Documentos do Cliente',
      description: 'Contratos, laudos e documentos vinculados ao cliente, com controle de visibilidade no portal.',
      icon: 'fa-folder-open',
      category: 'comercial',
      premium: true
    },
    {
      id: 10,
      title: 'Portal do Cliente',
      description: 'Seu cliente acessa boletos, histórico financeiro e documentos online — sem precisar te ligar.',
      icon: 'fa-user-circle',
      category: 'comercial',
      premium: true,
      highlight: true
    },

    // FINANCEIRO
    {
      id: 11,
      title: 'Contas a Pagar',
      description: 'Nunca pague em atraso: alertas de vencimento, fluxo de caixa futuro e baixa automática integrada ao banco.',
      icon: 'fa-money-bill-wave',
      category: 'financeiro'
    },
    {
      id: 12,
      title: 'Contas a Receber',
      description: 'Controle de inadimplência, parcelamentos e cobranças automáticas — seu caixa sempre atualizado.',
      icon: 'fa-hand-holding-usd',
      category: 'financeiro'
    },
    {
      id: 13,
      title: 'Contas Bancárias',
      description: 'Gerencie múltiplas contas em uma visão unificada com saldo em tempo real e transferências internas.',
      icon: 'fa-university',
      category: 'financeiro'
    },
    {
      id: 14,
      title: 'Centro de Custos',
      description: 'Distribua receitas e despesas por departamento ou projeto e descubra o que realmente é lucrativo.',
      icon: 'fa-project-diagram',
      category: 'financeiro'
    },
    {
      id: 15,
      title: 'Categorias Financeiras',
      description: 'Classifique cada movimentação e gere relatórios que mostram exatamente onde o dinheiro está indo.',
      icon: 'fa-tags',
      category: 'financeiro'
    },
    {
      id: 16,
      title: 'Extrato Financeiro',
      description: 'Visão completa do fluxo de caixa com filtros por período, categoria, conta e centro de custo.',
      icon: 'fa-file-invoice-dollar',
      category: 'financeiro'
    },
    {
      id: 17,
      title: 'Conciliação Bancária (OFX)',
      description: 'Importe o extrato do banco e concilie lançamentos automaticamente — elimine horas de trabalho manual.',
      icon: 'fa-sync-alt',
      category: 'financeiro',
      highlight: true
    },
    {
      id: 18,
      title: 'Cobrança Automática via Asaas',
      description: 'Emita boleto ou PIX para seus clientes direto pelo sistema e receba a baixa automática após o pagamento.',
      icon: 'fa-barcode',
      category: 'financeiro',
      premium: true,
      highlight: true
    },
    {
      id: 19,
      title: 'DRE (Demonstrativo de Resultados)',
      description: 'Veja lucro, custo e resultado operacional consolidados por período — a visão que o seu contador te cobra.',
      icon: 'fa-chart-area',
      category: 'financeiro',
      highlight: true
    },
    {
      id: 39,
      title: 'Regime de Competência ou Caixa',
      description: 'Escolha como registrar seus lançamentos: competência (quando ocorre) ou caixa (quando paga/recebe).',
      icon: 'fa-balance-scale',
      category: 'financeiro'
    },

    // OPERACIONAL
    {
      id: 20,
      title: 'Gestão de Produtos',
      description: 'Catálogo completo com variações, código de barras, custo e margem — controle real do que você vende.',
      icon: 'fa-boxes',
      category: 'operacional'
    },
    {
      id: 40,
      title: 'Catálogo Público de Produtos',
      description: 'Seu catálogo online, sem precisar de dev: exiba produtos, preços e receba pedidos direto pelo sistema.',
      icon: 'fa-store',
      category: 'operacional',
      premium: true,
      highlight: true
    },
    {
      id: 41,
      title: 'Múltiplos Depósitos',
      description: 'Gerencie estoque em diferentes locais com transferências e rastreabilidade completa.',
      icon: 'fa-warehouse',
      category: 'operacional',
      premium: true
    },
    {
      id: 21,
      title: 'Gestão de Serviços',
      description: 'Cadastre serviços com tributos específicos e precificação própria, pronto para emissão de NFS-e.',
      icon: 'fa-tools',
      category: 'operacional'
    },
    {
      id: 22,
      title: 'Gestão de Compras',
      description: 'Registre compras de fornecedores, atualize o estoque automaticamente e controle o custo de cada item.',
      icon: 'fa-shopping-basket',
      category: 'operacional'
    },
    {
      id: 23,
      title: 'Ordens de Serviço',
      description: 'Abra, execute e fature ordens de serviço com histórico de execução e geração automática de NFS-e.',
      icon: 'fa-clipboard-list',
      category: 'operacional'
    },
    {
      id: 24,
      title: 'Equipamentos',
      description: 'Inventário de máquinas e ferramentas com histórico de uso, manutenções e vinculação a obras ou clientes.',
      icon: 'fa-cogs',
      category: 'operacional'
    },
    {
      id: 25,
      title: 'Módulo de Obras',
      description: 'Gerencie canteiros de obras do início ao fechamento: registros diários, permutas e relatório pré-faturamento.',
      icon: 'fa-hard-hat',
      category: 'operacional',
      premium: true,
      highlight: true
    },
    {
      id: 26,
      title: 'Módulo de Veículos',
      description: 'Frota sob controle: abastecimentos, manutenções e custo por quilômetro rodado — tudo em um lugar só.',
      icon: 'fa-truck',
      category: 'operacional',
      premium: true
    },

    // FISCAL
    {
      id: 27,
      title: 'Emissão de NF-e',
      description: 'Emita notas fiscais de produto com transmissão direta ao SEFAZ — sem sair do sistema, sem retrabalho.',
      icon: 'fa-file-invoice',
      category: 'fiscal',
      highlight: true
    },
    {
      id: 28,
      title: 'Emissão de NFS-e',
      description: 'NFS-e integrada a prefeituras de todo o Brasil — emita nota de serviço em segundos com impostos calculados.',
      icon: 'fa-file-contract',
      category: 'fiscal',
      highlight: true
    },
    {
      id: 29,
      title: 'Cancelamento de Notas',
      description: 'Cancele NF-e ou NFS-e com protocolo legal registrado — sem precisar acessar portais externos.',
      icon: 'fa-times-circle',
      category: 'fiscal'
    },
    {
      id: 30,
      title: 'Cálculo Automático de Impostos',
      description: 'ICMS, IPI, PIS, COFINS e ISS calculados automaticamente por NCM — sem erro, sem multa, sem contador manual.',
      icon: 'fa-calculator',
      category: 'fiscal'
    },
    {
      id: 31,
      title: 'Monitoramento de NF-e Recebidas',
      description: 'Detecta automaticamente notas emitidas contra seu CNPJ e lança a despesa no contas a pagar — zero digitação.',
      icon: 'fa-radar',
      category: 'fiscal',
      premium: true,
      highlight: true
    },

    // ADMINISTRATIVO
    {
      id: 42,
      title: 'Multi-empresa',
      description: 'Gerencie múltiplas empresas com um único login — troque de CNPJ em segundos e mantenha dados 100% separados.',
      icon: 'fa-layer-group',
      category: 'administrativo',
      highlight: true
    },
    {
      id: 32,
      title: 'Gestão de Colaboradores',
      description: 'Cadastro de colaboradores com cargos, salários, documentos e vinculação a obras ou ordens de serviço.',
      icon: 'fa-users-cog',
      category: 'administrativo'
    },
    {
      id: 33,
      title: 'Usuários e Permissões',
      description: 'Controle exatamente o que cada usuário pode ver e fazer — por módulo, por ação e por empresa.',
      icon: 'fa-user-shield',
      category: 'administrativo'
    },
    {
      id: 34,
      title: 'Configurações da Empresa',
      description: 'Dados fiscais, cenários de tributação, logotipo e parâmetros do sistema configurados do jeito que você precisa.',
      icon: 'fa-building',
      category: 'administrativo'
    },
    {
      id: 35,
      title: 'Dashboard Personalizado',
      description: 'Incorpore seu Power BI, Metabase ou Looker Studio direto no sistema — seus indicadores onde o time trabalha.',
      icon: 'fa-chart-line',
      category: 'administrativo',
      premium: true
    },

    // INTEGRAÇÃO
    {
      id: 36,
      title: 'Importação de XML de NF-e',
      description: 'Importe XML de compras de fornecedores e dê entrada no estoque e no financeiro automaticamente.',
      icon: 'fa-file-import',
      category: 'integracao'
    },
    {
      id: 37,
      title: 'API REST',
      description: 'Integre seu e-commerce, app mobile ou sistema legado via API documentada e segura.',
      icon: 'fa-code',
      category: 'integracao',
      premium: true
    },
    {
      id: 38,
      title: 'Backup em Nuvem',
      description: 'Seus dados protegidos com backup automático diário — restauração sob demanda sem perda de informação.',
      icon: 'fa-cloud-upload-alt',
      category: 'integracao'
    }
  ];

  const filteredFeatures = activeCategory === 'all' 
    ? features 
    : features.filter(f => f.category === activeCategory);

  return (
    <div className="features-page">
      {/* Hero Section */}
      <section className="features-hero">
        <div className="features-hero__container">
          <h1 className="features-hero__title">
            Um sistema. Tudo que sua empresa precisa para crescer.
          </h1>
          <p className="features-hero__subtitle">
            Mais de 40 funcionalidades integradas — do comercial ao fiscal, do financeiro ao operacional —
            para você parar de apagar incêndio e começar a gerir de verdade.
          </p>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="features-categories">
        <div className="features-categories__container">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`features-categories__item ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat.id)}
            >
              <i className={`fas ${cat.icon}`}></i>
              <span>{cat.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section className="features-grid">
        <div className="features-grid__container">
          {filteredFeatures.map(feature => (
            <div
              key={feature.id}
              className={[
                'feature-card',
                feature.highlight ? 'feature-card--highlight' : '',
                feature.premium ? 'feature-card--premium' : ''
              ].filter(Boolean).join(' ')}
            >
              {feature.highlight && !feature.premium && (
                <div className="feature-card__badge feature-card__badge--highlight">
                  <i className="fas fa-star"></i>
                  <span>Destaque</span>
                </div>
              )}

              {feature.premium && (
                <div className={`feature-card__badge ${feature.highlight ? 'feature-card__badge--highlight' : ''}`}>
                  <i className="fas fa-crown"></i>
                  <span>{feature.highlight ? 'Destaque' : 'Premium'}</span>
                </div>
              )}

              <div className="feature-card__icon">
                <i className={`fas ${feature.icon}`}></i>
              </div>

              <h3 className="feature-card__title">{feature.title}</h3>
              <p className="feature-card__description">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="features-cta">
        <div className="features-cta__container">
          <h2 className="features-cta__title">
            Pronto para revolucionar sua gestão?
          </h2>
          <p className="features-cta__description">
            Teste gratuitamente por 7 dias todas as funcionalidades premium
          </p>
          <div className="features-cta__buttons">
            <a 
              href="https://app.77gestao.com.br/cadastro?source=features" 
              className="features-cta__button features-cta__button--primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fas fa-rocket"></i>
              Iniciar teste grátis
            </a>
            <a 
              href="#pricing" 
              className="features-cta__button features-cta__button--secondary"
            >
              Ver planos e preços
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Features;
